import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createLesson,
  CreateLessonPayload,
  deleteLesson,
  getLessonById,
  getLessonList,
  getWeekList,
  LessonDetails,
  LessonWeek,
  rearrangeLesson,
  RearrangeLessonPayload,
  updateLesson,
  Week,
} from "../../apis/lessonApis";

/* ================= STATE ================= */

interface LessonState {
  weeks: LessonWeek[];
  weekList: Week[];
  lessonDetails: LessonDetails | null;
  loading: boolean;
  error: string | null;
}

const initialState: LessonState = {
  weeks: [],
  weekList: [],
  lessonDetails: null,
  loading: false,
  error: null,
};

/* ================= THUNKS ================= */

/* ===== LESSON LIST ===== */
export const fetchLessonListThunk = createAsyncThunk<
  LessonWeek[],
  void,
  { rejectValue: string }
>("lesson/fetchList", async (_, { rejectWithValue }) => {
  try {
    const response = await getLessonList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch lessons");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to fetch lessons",
    );
  }
});

/* ===== WEEK LIST ===== */
export const fetchWeekListThunk = createAsyncThunk<
  Week[],
  void,
  { rejectValue: string }
>("lesson/fetchWeekList", async (_, { rejectWithValue }) => {
  try {
    const response = await getWeekList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch weeks");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to fetch weeks",
    );
  }
});

/* ===== CREATE LESSON ===== */
export const createLessonThunk = createAsyncThunk<
  { status: boolean; data: string },
  CreateLessonPayload,
  { rejectValue: string }
>("lesson/create", async (payload, { rejectWithValue }) => {
  try {
    const response = await createLesson(payload);

    if (!response.status) {
      return rejectWithValue("Failed to create lesson");
    }

    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to create lesson",
    );
  }
});

/* ===== GET LESSON BY ID ===== */
export const fetchLessonByIdThunk = createAsyncThunk<
  LessonDetails,
  string,
  { rejectValue: string }
>("lesson/fetchById", async (lessonId, { rejectWithValue }) => {
  try {
    const response = await getLessonById(lessonId);

    if (!response.status) {
      return rejectWithValue("Failed to fetch lesson details");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to fetch lesson details",
    );
  }
});

/* ===== REARRANGE LESSON ===== */
export const rearrangeLessonThunk = createAsyncThunk<
  { status: boolean; data: string },
  RearrangeLessonPayload,
  { rejectValue: string }
>("lesson/rearrange", async (payload, { rejectWithValue }) => {
  try {
    const response = await rearrangeLesson(payload);

    if (!response.status) {
      return rejectWithValue("Failed to rearrange lesson");
    }

    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to rearrange lesson",
    );
  }
});

/* ===== UPDATE LESSON ===== */
export const updateLessonThunk = createAsyncThunk<
  { status: boolean; data: string },
  { lessonId: string; payload: CreateLessonPayload },
  { rejectValue: string }
>("lesson/update", async ({ lessonId, payload }, { rejectWithValue }) => {
  try {
    const response = await updateLesson(lessonId, payload);

    if (!response.status) {
      return rejectWithValue("Failed to update lesson");
    }

    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to update lesson",
    );
  }
});

/* ===== DELETE LESSON ===== */
export const deleteLessonThunk = createAsyncThunk<
  { status: boolean; data: string },
  string,
  { rejectValue: string }
>("lesson/delete", async (lessonId, { rejectWithValue }) => {
  try {
    const response = await deleteLesson(lessonId);

    if (!response.status) {
      return rejectWithValue("Failed to delete lesson");
    }

    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.data || "Failed to delete lesson",
    );
  }
});

/* ================= SLICE ================= */

const lessonSlice = createSlice({
  name: "lesson",
  initialState,
  reducers: {
    resetLessonState: () => initialState,
    clearLessonDetails: (state) => {
      state.lessonDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ===== LESSON LIST ===== */
      .addCase(fetchLessonListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLessonListThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.weeks = action.payload;
      })
      .addCase(fetchLessonListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Something went wrong";
      })

      /* ===== WEEK LIST ===== */
      .addCase(fetchWeekListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeekListThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.weekList = action.payload.sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
      })
      .addCase(fetchWeekListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch weeks";
      })

      /* ===== CREATE LESSON ===== */
      .addCase(createLessonThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLessonThunk.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(action.payload.data || "Lesson created successfully");
      })
      .addCase(createLessonThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to create lesson";
        toast.error(state.error);
      })

      /* ===== UPDATE LESSON ===== */
      .addCase(updateLessonThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLessonThunk.fulfilled, (state, action) => {
        state.loading = false;
        toast.success("Lesson updated successfully");
      })
      .addCase(updateLessonThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to update lesson";
        toast.error(state.error);
      })

      /* ===== LESSON DETAILS ===== */
      .addCase(fetchLessonByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lessonDetails = null;
      })
      .addCase(fetchLessonByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.lessonDetails = action.payload;
      })
      .addCase(fetchLessonByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch lesson details";
        toast.error(state.error);
      })

      /* ===== REARRANGE LESSON ===== */
      .addCase(rearrangeLessonThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rearrangeLessonThunk.fulfilled, (state, action) => {
        state.loading = false;
        toast.success("Lesson order Updated");
      })
      .addCase(rearrangeLessonThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to rearrange lesson";
        toast.error(state.error);
      })

      /* ===== DELETE LESSON ===== */
      .addCase(deleteLessonThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLessonThunk.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(action.payload.data);
      })
      .addCase(deleteLessonThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to delete lesson";
        toast.error(state.error);
      });
  },
});

/* ================= EXPORTS ================= */

export const { resetLessonState, clearLessonDetails } = lessonSlice.actions;

export default lessonSlice.reducer;
